/**
 * SN_SAI_TRM_REG_001 — 고객사 용어 관리 (거래처) (2026-09-18, 2차 검토 확정)
 *
 * 왼쪽 용어 목록(sai_dd) — 용어명 · 요약 · 모듈 · 사용여부. 행을 고르면 오른쪽 폼에 상세(설명 포함).
 * 오른쪽 폼에서 고친 값은 gridref 로 그리드 행에 되돌아가고, 툴바 [저장] 이 그리드 변경을 보낸다. 용어코드는 서버가 채번한다.
 * [챗봇에서 가져오기] — 팝업. 챗봇이 못 알아들은 말(sai_dd_cand)을 여러 개 골라 [적용] 하면 사용여부 N 으로 왼쪽에 들어온다. 폼에서 다듬고 켠다.
 *   팝업 그리드의 쓰레기통 = 무시(다시 안 올라옴). 지우지 않는다 — "쓰지 않음" 은 사용여부를 끄는 것.
 *
 * 조회조건 · 그리드 컬럼 · 폼 · 팝업 정의는 DB(sys_formctrl · sys_formgridcolumn · sys_popup*, 08_AI_화면설정.sql)에서 온다. 조회는 sys_query sn_sai_trm_reg_001_01.
 * 본 : 사업장등록 SN_MAS_ORG_REG_002 (좌 그리드 · 우 SharedForm) · 제품 지식 관리와 같은 틀
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import SearchPanel from "@widgets/SearchPanel";
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { showPopupModal } from "@widgets/CommonModal";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnButton from "@shared/components/SnButton";
import SharedForm from "@widgets/SharedForm";
import { menuInfo } from "@shared/type/CommonType";
import SN_SAI_TRM_REG_001_POP_001 from "./SN_SAI_TRM_REG_001_POP_001";

interface Props {
  Menuprops: menuInfo;
}

const SN_SAI_TRM_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });
  const sharedFormRef1 = useRef<any>(null);
  const [sharedForm, setSharedForm] = useState<any[]>([]);
  const [focusedRowData, setFocusedRowData] = useState<any>();

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          grid1: rtn.grid.grid1 || [],
        });
        setSharedForm(rtn.form?.freeform1 || []);
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch, onInsert, onDelete, onSave,
  }));

  const onSearch = async () => {
    if (
      gridRefs.current["grid1"].current.getChange().modifiedRowcount > 0
    ) {
      const confirm = await showDialog("confirmCancel", "수정된 내용이 있습니다. 저장하시겠습니까?");
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      /* 다시 읽기 전에 수정 표시를 지운다. 안 지우면 아니오를 눌러도 "수정 중" 으로 남는다 */
      gridRefs.current["grid1"].current?.resetUpdate?.();
      const params = {
        id: "sn_sai_trm_reg_001_01",
        params: searchRef.current.getFormdata(),
      };
      setFocusedRowData(null);
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data?.[0] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      gridRefs.current["grid1"].current?.addRow();
    } catch (error: any) {}
  };
  const onDelete = async () => {
    await showDialog("alter", "용어는 삭제하지 않습니다. 사용여부를 끄고 저장해 주세요.");
  };
  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      if (!gridRefs.current["grid1"].current.requiredValuesCheck()) return false;
      const sai_ddChanges = gridRefs.current["grid1"].current.getChange();
      if (sai_ddChanges.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }
      const saveData: any = {
        sai_dd: sai_ddChanges,
      };
      const ok = await fnSave("SN_SAI_TRM_REG_001_SAVE_01", saveData);
      if (!ok) return false;
      gridRefs.current["grid1"].current.resetUpdate();
      await onSearch();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current?.resetUpdate(true);
      return false;
    }
  };
  /** 행추가 기본값 — 회사 · 사용횟수 0. 고정값(사용여부 등)은 sys_formgridcolumn.cd_value 가 넣는다 */
  const onBeforeAddTerm = (): any => {
    return { cd_firm: userInfo.cd_firm, qt_used: 0 };
  };
  /** [챗봇에서 가져오기] — 팝업에서 후보를 골라 [적용]. 사용여부 N 으로 들어온 뒤 다시 조회 */
  const onImportPopup = async () => {
    if (gridRefs.current["grid1"].current.getChange().modifiedRowcount > 0) {
      await showDialog("alter", "수정 중인 내용을 먼저 저장해 주세요.");
      return;
    }
    const result: any = await showPopupModal({
      title: "챗봇에서 가져오기",
      children: <SN_SAI_TRM_REG_001_POP_001 />,
      width: "80%",
      height: "80vh",
      okayBtnText: "적용",
    });
    if (!result || result === "close") return;
    const words: string[] = Array.isArray(result) ? result.flat().filter(Boolean) : [];
    if (words.length === 0) return;
    const res = await callApi("SN_SAI_TRM_REG_001_btnRegister", { words });
    if (!res?.status) return;
    await onSearch();
  };
  /** 왼쪽에서 행을 고르면 오른쪽 폼에 그 행. 폼에서 고친 값은 gridref 로 그리드 행에 되돌아간다 — 저장은 그리드 변경으로 간다 */
  const onFocusedRowChanged = useCallback(
    (row: any | null) => {
      setFocusedRowData(row);
    },
    [focusedRowData]
  );
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddTerm,
          onFocusedRowChanged: onFocusedRowChanged
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel ref={searchRef} data={formData} controls={formControlData} />
      </div>
      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <SnResizeBar direction={"width"} id="panelList" orgSize="23.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "용어", isEditable: false }}
            events={grids.grid1.events}
          />
        </SnResizeBar>
        <SnResizeBar direction={"width"} id="panelDetail" orgSize="76.5%">
          <SharedForm
            ref={sharedFormRef1}
            data={focusedRowData}
            controls={sharedForm}
            gridref={gridRefs.current["grid1"]}
            title="상세정보"
            header={
              <div className="flex justify-end">
                <SnButton text="챗봇에서 가져오기" onClick={() => { onImportPopup(); }} />
              </div>
            }
          ></SharedForm>
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_SAI_TRM_REG_001;
